import { get } from 'lodash';
import queryString from 'query-string';
import { useRef } from 'react';
import { Dimensions, Platform } from 'react-native';

import { IVideo } from '@nocode/types';
import { getItemListClick, getValueBinding } from '../../shared';
import defaultImage from './VideoPlayholder';

const checkIsCanvas = () => {
  const isWeb = Platform.OS === 'web';

  if (!isWeb) return false;

  const search = queryString.parse(window?.location?.search);
  const target = search?.target;

  return !target;
};

export const useVideoHook = (props: IVideo) => {
  const {
    attributes,
    width,
    height,
    isInCustomList,
    onPress,
    record,
    data,
    id,
    initId,
    style,
  } = props;
  const { successActions, action } = attributes;
  const {
    backgroundColor,
    opacity,
    borderColor,
    borderRadius,
    borderStyle,
    borderWidth,
  } = style || {};
  // Prioritize root level width/height over style object width/height
  const finalWidth = width || style?.width;
  const finalHeight = height || style?.height;
  // Apply style object properties for other styling with fallback to attributes
  const finalBackgroundColor =
    style?.backgroundColor || attributes.backgroundColor;
  const finalOpacity =
    style?.opacity !== undefined
      ? style.opacity
      : attributes.opacity !== undefined
      ? attributes.opacity
      : opacity;
  const finalBorderColor =
    style?.borderColor || attributes.borderColor || borderColor;
  const finalBorderRadius =
    style?.borderRadius !== undefined
      ? style.borderRadius
      : attributes.borderRadius !== undefined
      ? attributes.borderRadius
      : borderRadius;
  const finalBorderStyle =
    style?.borderStyle || attributes.borderStyle || borderStyle;
  const finalBorderWidth =
    style?.borderWidth !== undefined
      ? style.borderWidth
      : attributes.borderWidth !== undefined
      ? attributes.borderWidth
      : borderWidth;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  let actionVideoPlayed = false;
  let actionVideoEnded = false;
  const isCanvas = checkIsCanvas();

  const bindingValue = getValueBinding(
    isInCustomList ? initId : id,
    isInCustomList ? record : data,
    // overwrite preview.image
    {
      ...props,
      attributes: {
        ...attributes,
        preview: {
          ...attributes.preview,
          image: attributes.preview?.imageUrl || attributes.preview?.image,
        },
      },
    }
  );
  const { showVideoControl, autoPlay, loop, muted, videoSource, fullscreen } =
    bindingValue as Pick<
      IVideo['attributes'],
      | 'autoPlay'
      | 'showVideoControl'
      | 'loop'
      | 'muted'
      | 'videoSource'
      | 'fullscreen'
    >;

  /** Fallback if video doesnt run */
  const videoUrl =
    typeof videoSource === 'string' && videoSource.trim() !== ''
      ? videoSource
      : (videoSource as any)?.videoUrl ||
        (attributes?.videoSource as any)?.videoUrl ||
        '';

  const usePreview = get(bindingValue, 'preview.enabled', false);
  const previewImage = usePreview
    ? get(bindingValue, 'preview.image', defaultImage)?.toString()
    : defaultImage;
  const poster = usePreview ? previewImage : undefined;

  const onVideoPlayed = () => {
    if (action && !actionVideoPlayed) {
      onPress(action?.actionId, {
        itemListClick: getItemListClick(record),
      });
      actionVideoPlayed = true;
    }
  };

  const onVideoEnded = () => {
    if (successActions && !actionVideoEnded) {
      onPress(successActions?.actionId, {
        itemListClick: getItemListClick(record),
      });
      actionVideoEnded = true;
    }
  };

  return {
    onVideoPlayed,
    onVideoEnded,
    style: {
      container: {
        width: finalWidth,
        height: finalHeight,
        backgroundColor: finalBackgroundColor,
        opacity: finalOpacity,
        ...(finalBorderStyle === 'none'
          ? {}
          : {
              borderColor: finalBorderColor,
              borderRadius: finalBorderRadius,
              borderStyle: finalBorderStyle,
              borderWidth: finalBorderWidth,
            }),
      },
      video: {
        ...((fullscreen
          ? {
              position: 'absolute',
            }
          : {}) as { position: 'absolute' | 'relative' | undefined }),
        width: '100%',
        height: '100%',
        backgroundColor: finalBackgroundColor,
      },
    },
    poster,
    previewImage,
    videoRef,
    videoOptions: {
      showVideoControl: !!showVideoControl,
      autoPlay: !!autoPlay,
      loop: !!loop,
      muted: !!muted,
      videoSource: videoUrl,
      fullscreen: !!fullscreen,
    },
  };
};
